import { type FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Button, H6, Box, H5, Stack, type BoxProps, Divider } from '@dhl-ui/components'
import { AddressLines } from '@dhl/components'
import { DhlLogo } from '@dhl/icons'

import { type Servicepoint } from './model.js'
import { ServicepointOpeningTimes } from './ServicepointOpeningTimes.js'
import { ServicepointMap } from './ServicepointMap.js'

export type ServicepointDetailsProps = {
  servicepoint: Servicepoint
  sx?: BoxProps['sx']
  onSelect?: (servicepoint: Servicepoint) => void
  selectButtonText?: string
}

export const ServicepointDetails: FC<ServicepointDetailsProps> = ({ servicepoint, onSelect, sx, selectButtonText }) => {
  const { t } = useTranslation()

  return (
    <Stack sx={{ flex: 1, height: '100%', overflow: 'hidden' }}>
      <Stack spacing={3} sx={{ overflowY: 'auto', flexGrow: 1, px: 0, pb: 2 }}>
        <Stack spacing={3} sx={sx}>
          <ServicepointMap servicepoint={servicepoint} />
          <Stack direction="row" px={2} spacing={2}>
            <DhlLogo sx={{ width: 40, height: 40 }} />

            <Stack>
              <H5>{servicepoint.name}</H5>
              <Body1>
                <AddressLines address={servicepoint.address} singleLine />
              </Body1>
            </Stack>
          </Stack>
        </Stack>

        <Divider />

        <Stack px={2} spacing={1}>
          <H6>{t('servicepoint.servicepointDetailsDialog.openingTimesText')}</H6>

          <ServicepointOpeningTimes openingTimes={servicepoint.openingsTimes} />
        </Stack>
      </Stack>

      {onSelect && (
        <Box sx={{ p: 2, borderTop: '1px solid transparent', borderTopColor: 'divider' }}>
          <Button
            fullWidth
            color="primary"
            data-testid="select-servicepoint-button"
            variant="contained"
            onClick={() => onSelect(servicepoint)}
          >
            {selectButtonText ??
              t(`servicepoint.servicepointDetailsDialog.selectButton.${servicepoint.servicepointType.toLowerCase()}`)}
          </Button>
        </Box>
      )}
    </Stack>
  )
}
